"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const clsx_1 = __importDefault(require("clsx"));
const models_1 = require("../../utils/models");
const utils_1 = require("../../utils/utils");
const search_1 = require("../../utils/hooks/search");
const useSize_1 = __importDefault(require("../../utils/useSize"));
const small_screen_view_1 = __importDefault(require("./small-screen-view"));
const large_screen_view_1 = __importDefault(require("./large-screen-view"));
const list_view_1 = __importDefault(require("./list-view"));
const search_box_view_1 = __importDefault(require("./search-box-view"));
const search_filters_1 = __importDefault(require("./search-filters"));
require("./styles.scss");
// export default
function LocationSearchResults({ getSearchResultComponent, locations, currentLocation, buttonLastClickedAt, searchBox, maxResults, hasApplicationHeader, filterItems, searchParamDefaults, disableFilter, searchResultsHeader, onCurrentLocationChange }) {
    const mapRef = (0, react_1.useRef)(null);
    const resultBounds = (0, react_1.useRef)(null);
    const resultBoundsPadding = (0, react_1.useRef)(0);
    const { t } = (0, react_i18next_1.useTranslation)();
    const { sizeWidth } = (0, useSize_1.default)();
    const mapResultsScroll = (0, react_1.useRef)(null);
    const resultsScroll = (0, react_1.useRef)(null);
    const [showMap, setShowMap] = (0, react_1.useState)(false);
    const [hovered, setHovered] = (0, react_1.useState)(null);
    const [searchResults, setSearchResults] = (0, react_1.useState)(null);
    const [selected, setSelected] = (0, react_1.useState)(null);
    const markerRefs = (0, react_1.useRef)(new Map());
    const searchResultRefs = (0, react_1.useRef)(new Map());
    const focusOnLocation = (0, react_1.useCallback)((newSelectedLocation) => {
        const selectedLocation = newSelectedLocation !== null && newSelectedLocation !== void 0 ? newSelectedLocation : selected;
        if (selectedLocation === null) {
            return;
        }
        const searchResultDiv = searchResultRefs.current.get(selectedLocation === null || selectedLocation === void 0 ? void 0 : selectedLocation.id);
        if (searchResultDiv) {
            searchResultDiv.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [selected]);
    (0, react_1.useEffect)(() => {
        var _a, _b, _c, _d, _e;
        const results = (0, utils_1.findLocations)(locations, currentLocation);
        setSelected(null);
        setSearchResults(maxResults ? results.splice(0, maxResults) : results);
        (_a = markerRefs.current) === null || _a === void 0 ? void 0 : _a.clear();
        // Scroll to top or left of search results
        (_b = mapResultsScroll.current) === null || _b === void 0 ? void 0 : _b.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
        });
        (_c = resultsScroll.current) === null || _c === void 0 ? void 0 : _c.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
        });
        if (results.length === 0) {
            resultBounds.current = new google.maps.LatLngBounds()
                .extend({
                lat: 41.6765556,
                lng: -95.1537399,
            })
                .extend({
                lat: 56.8593635,
                lng: -74.3201062,
            });
            resultBoundsPadding.current = 0;
        }
        else {
            const newBounds = ((_d = (0, utils_1.getSuggestedViewport)(currentLocation)) !== null && _d !== void 0 ? _d : new google.maps.LatLngBounds())
                .extend({
                lat: results[0].lat,
                lng: results[0].lng,
            })
                .extend({
                lat: currentLocation.lat,
                lng: currentLocation.lng,
            });
            resultBounds.current = newBounds;
            resultBoundsPadding.current = 50;
        }
        (_e = mapRef.current) === null || _e === void 0 ? void 0 : _e.fitBounds(resultBounds.current, resultBoundsPadding.current);
    }, [currentLocation, locations, setSelected, setSearchResults, markerRefs, mapResultsScroll, resultsScroll, resultBounds, resultBoundsPadding, mapRef, maxResults]);
    (0, react_1.useEffect)(() => {
        var _a, _b, _c;
        if (searchResults && searchResults.length > 0) {
            (0, utils_1.setBoundsToInclude)(searchResults[0], mapRef, currentLocation);
        }
        // Scroll to top or left of search results
        (_a = mapResultsScroll.current) === null || _a === void 0 ? void 0 : _a.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
        });
        (_b = resultsScroll.current) === null || _b === void 0 ? void 0 : _b.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
        });
        if (resultBounds.current) {
            (_c = mapRef.current) === null || _c === void 0 ? void 0 : _c.fitBounds(resultBounds.current, resultBoundsPadding.current);
        }
    }, [buttonLastClickedAt, searchResults, setSelected, utils_1.setBoundsToInclude, mapResultsScroll, resultsScroll, resultBounds, mapRef, resultBoundsPadding]);
    // Handle scroll restoration when leaving and returning to search page
    (0, react_1.useEffect)(() => {
        const scrollDiv = mapResultsScroll === null || mapResultsScroll === void 0 ? void 0 : mapResultsScroll.current;
        const scrollPosition = sessionStorage.getItem('scrollPosition');
        if (scrollPosition && scrollDiv) {
            scrollDiv.scrollTop = parseInt(scrollPosition, 10);
        }
        const handleScroll = () => {
            sessionStorage.setItem('scrollPosition', (scrollDiv === null || scrollDiv === void 0 ? void 0 : scrollDiv.scrollTop.toString()) || '0');
        };
        scrollDiv === null || scrollDiv === void 0 ? void 0 : scrollDiv.addEventListener('scroll', handleScroll);
        return () => {
            scrollDiv === null || scrollDiv === void 0 ? void 0 : scrollDiv.removeEventListener('scroll', handleScroll);
        };
    }, [searchResults, mapResultsScroll, resultsScroll]);
    (0, react_1.useEffect)(() => focusOnLocation(selected !== null && selected !== void 0 ? selected : undefined), [focusOnLocation, selected, showMap]);
    const searchFilterParams = (0, search_1.useSearchFilterParams)();
    const filterCountText = (0, react_1.useMemo)(() => {
        let count = 0;
        for (let k in searchFilterParams) {
            const param = searchFilterParams[k];
            if (param.includeInFilterCount) {
                if (param.filter === models_1.Filter.Multiselect) {
                    count += param.values.length;
                }
                else {
                    param.values.forEach(p => {
                        if (p === '1') {
                            count += 1;
                        }
                    });
                }
            }
        }
        return count > 0 ? `(${count})` : '';
    }, [searchFilterParams]);
    const [showFilter, setShowFilter] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        if (onCurrentLocationChange) {
            onCurrentLocationChange(currentLocation);
        }
    }, [currentLocation]);
    const filter = (0, react_1.useMemo)(() => {
        const fTitle = `${t('searchFilters.title')} ${filterCountText}`;
        return (filterItems === null || filterItems === void 0 ? void 0 : filterItems.length) ? ((0, jsx_runtime_1.jsx)("div", Object.assign({ className: "filter" }, { children: (0, jsx_runtime_1.jsxs)("button", Object.assign({ className: "ontario-button ontario-button--secondary", onClick: () => {
                    setShowFilter(!showFilter);
                } }, { children: [(0, jsx_runtime_1.jsx)("svg", Object.assign({ className: "ontario-icon", "aria-hidden": "true", focusable: "false", viewBox: "0 0 24 24", preserveAspectRatio: "xMidYMid meet" }, { children: (0, jsx_runtime_1.jsx)("use", { xlinkHref: require("../../assets/ontario-icons-secondary.svg") + "#ontario-icon-filter" }) })), fTitle] })) }))) : undefined;
    }, [showFilter, setShowFilter, filterItems]);
    const showSmallScreenView = (0, react_1.useCallback)(() => {
        if (searchResults && searchResults.length > 0 && sizeWidth <= 767) {
            return (0, jsx_runtime_1.jsx)(small_screen_view_1.default, { filter: filter, showMap: showMap, setShowMap: setShowMap, selected: selected, setSelected: setSelected, searchResults: searchResults });
        }
    }, [filter, sizeWidth, searchResults, selected, setSelected, showMap, setShowMap]);
    const showLargeScreenView = (0, react_1.useCallback)(() => {
        return (0, jsx_runtime_1.jsx)(large_screen_view_1.default, { searchResults: searchResults, mapRef: mapRef, selected: selected, setSelected: setSelected, currentLocation: currentLocation, resultBounds: resultBounds, resultBoundsPadding: resultBoundsPadding, markerRefs: markerRefs, focusOnLocation: focusOnLocation, hovered: hovered, setHovered: setHovered, showMap: showMap, sizeWidth: sizeWidth });
    }, [sizeWidth, searchResults, mapRef, showMap, selected, setSelected, currentLocation, resultBounds, resultBoundsPadding, focusOnLocation, markerRefs, hovered, setHovered]);
    const showListView = (0, react_1.useCallback)(() => {
        if (searchResults && searchResults.length > 0) {
            return (0, jsx_runtime_1.jsx)(list_view_1.default, { getSearchResultComponent: getSearchResultComponent, searchResults: searchResults, currentLocation: currentLocation, mapResultsScroll: mapResultsScroll, resultsScroll: resultsScroll, showMap: showMap, mapRef: mapRef, hovered: hovered, setHovered: setHovered, selected: selected, setSelected: setSelected, focusOnLocation: focusOnLocation, searchResultRefs: searchResultRefs, hasApplicationHeader: filterItems && hasApplicationHeader, searchResultsHeader: searchResultsHeader });
        }
        else {
            return (0, jsx_runtime_1.jsx)("div", Object.assign({ className: "map-results-scroll" }, { children: (0, jsx_runtime_1.jsx)(react_i18next_1.Trans, Object.assign({ t: t, i18nKey: "mapNoResults" }, { children: (0, jsx_runtime_1.jsx)("div", Object.assign({ className: "map-no-results" }, { children: (0, jsx_runtime_1.jsx)("h5", { children: "Sorry, we couldn\u2019t find any matches." }) })) })) }));
        }
    }, [getSearchResultComponent, searchResults, currentLocation, mapResultsScroll, resultsScroll, showMap, mapRef, selected, setSelected, focusOnLocation, searchResultRefs, hovered, setHovered, t]);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("div", Object.assign({ className: (0, clsx_1.default)("search-results-container", {
                    showMap,
                }) }, { children: [(0, jsx_runtime_1.jsx)(search_box_view_1.default, { sizeWidth: sizeWidth, filter: filter, searchBox: searchBox }), showSmallScreenView(), showLargeScreenView(), showListView()] })), filterItems && showFilter &&
                (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { className: "search-results-overlay" }), (0, jsx_runtime_1.jsx)(search_filters_1.default, { searchParamDefaults: searchParamDefaults, setShowFilter: setShowFilter, filterItems: filterItems, hasApplicationHeader: hasApplicationHeader, currentLocation: currentLocation, disableFilter: disableFilter, mapResultsScroll: mapResultsScroll, resultsScroll: resultsScroll })] })] }));
}
exports.default = LocationSearchResults;
